// milepostAxis.js — map a lane's ARC axis (0 → totalMi, drawn order) onto the
// state's MILEPOST axis, so the Route XML declares the mileposts the user
// actually selected instead of raw arc miles.
//
// Why this exists: the XML carries exactly ONE mileage axis. `BEGIN_MILE` →
// `END_MILE` is a linear reparametrisation of the `<POINTS>` polyline — the
// engine interpolates every station's mileage between them (see
// `_mile_of_point` in Backend/Pipeline/routes/rsa_analysis_planner.py), and
// 123bimlive meters billing on `END_MILE − BEGIN_MILE`
// (`_compute_directional_miles`). So the axis has to be resolved once for the
// whole lane; the speed-summary table's per-row "MP here, arc miles there"
// display can't be copied into the XML row-by-row.
//
// Where the milepost data comes from:
//   • "Load speeds from LRS" → `buildSpeedSegmentsFromLrs` stamps `milepost` on
//     every segment (and `milepostEnd` on the last one).
//   • "Add route by road / milepost" → the user types the range by hand; it
//     lands on the parent PATH as `lrs.start.measure` / `lrs.end.measure`
//     (v2) or `lrs.mpStart` / `lrs.mpEnd` (v1).
//   • Editing a boundary mile nulls that segment's `milepost`
//     (`updateSpeedBreakpointMile`), and a breakpoint added from the map has no
//     `milepost` key at all (`addSpeedBreakpoint`) — so partial coverage is the
//     normal state of a lane the user has touched, not an edge case.
//
// A milepost span is a CALIBRATED measure, not a physical distance: markers
// aren't repositioned when a road is realigned, so the MP span and the lane's
// true arc length legitimately differ by a few percent (see `lengthMismatch` in
// lrsMath.js). That difference intentionally flows through to the billed miles
// — the mileposts the user selected are the numbers of record.

/** Finite-number coercion that never turns null/''/undefined into 0.
 *  `Number(null) === 0` would silently invent milepost 0. */
function finiteMp(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The parent path's manually typed milepost range, as an [start, end] pair.
 *  Null for a v2 route that spans two different roads — there is no single
 *  milepost axis across a road change. Mirrors `expectedSpanFromLrs` in
 *  lrsMath.js, but keeps the SIGNED endpoints instead of the absolute span. */
function pathMilepostRange(lrs) {
  if (!lrs) return null;
  const [start, end] =
    lrs.v === 2
      ? lrs.sameRoad
        ? [lrs.start?.measure, lrs.end?.measure]
        : [null, null]
      : [lrs.mpStart, lrs.mpEnd];
  const s = finiteMp(start);
  const e = finiteMp(end);
  return s == null || e == null ? null : [s, e];
}

/** Ordered, de-duplicated [arcMile, milepost] anchors carried by the segments.
 *  Every segment contributes its start milepost; the last one also contributes
 *  `milepostEnd` at the far end of the lane. */
function segmentAnchors(speedSegments, totalMi) {
  const segs = (Array.isArray(speedSegments) ? speedSegments : [])
    .filter((seg) => Number.isFinite(Number(seg?.mileMark)))
    .sort((a, b) => Number(a.mileMark) - Number(b.mileMark));

  const anchors = [];
  const push = (arc, mp) => {
    if (mp == null || !Number.isFinite(arc)) return;
    // Two anchors at the same arc position would make a zero-width span; keep
    // the first and ignore the duplicate.
    if (anchors.length && Math.abs(anchors[anchors.length - 1][0] - arc) < 1e-9) return;
    anchors.push([arc, mp]);
  };

  segs.forEach((seg) => push(Number(seg.mileMark), finiteMp(seg.milepost)));
  if (segs.length) push(totalMi, finiteMp(segs[segs.length - 1].milepostEnd));
  return anchors;
}

/** Strictly ascending or strictly descending in the milepost component. */
function strictlyMonotone(anchors) {
  if (anchors.length < 2) return false;
  const ascending = anchors[anchors.length - 1][1] > anchors[0][1];
  for (let i = 1; i < anchors.length; i += 1) {
    const delta = anchors[i][1] - anchors[i - 1][1];
    if (ascending ? delta <= 0 : delta >= 0) return false;
  }
  return true;
}

/** Piecewise-linear arc → milepost interpolator over `anchors`.
 *  Positions between two anchors interpolate; positions outside the outermost
 *  anchors extrapolate along the nearest anchor pair's slope. */
function piecewise(anchors) {
  return (arcMile) => {
    const x = Number(arcMile);
    if (!Number.isFinite(x)) return null;

    let lo = 0;
    let hi = anchors.length - 1;
    if (x <= anchors[0][0]) {
      hi = 1; // extrapolate backwards off the first pair
    } else if (x >= anchors[anchors.length - 1][0]) {
      lo = anchors.length - 2; // extrapolate forwards off the last pair
      hi = anchors.length - 1;
    } else {
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (anchors[mid][0] <= x) lo = mid;
        else hi = mid;
      }
    }
    if (x <= anchors[0][0]) lo = 0;

    const [x0, mp0] = anchors[lo];
    const [x1, mp1] = anchors[hi];
    const span = x1 - x0;
    if (!(Math.abs(span) > 1e-12)) return mp0;
    return mp0 + ((x - x0) / span) * (mp1 - mp0);
  };
}

/**
 * Resolve the milepost axis for one lane.
 *
 * @param speedSegments  the lane's `speedSegments` ([{ mileMark, speedMph,
 *                       milepost?, milepostEnd? }]) — the rows the speed
 *                       summary renders.
 * @param totalMi        the lane's arc length in miles.
 * @param lrs            the PARENT PATH's `lrs` object, if any — the fallback
 *                       source when no segment carries a milepost.
 * @param direction      "P" or "N". Guards the axis against emitting an order
 *                       that contradicts the declared direction.
 * @returns `{ beginMile, endMile, toAxis }` — `toAxis(arcMile)` gives the
 *          milepost at that arc position — or `null` when the lane has no
 *          usable milepost data, in which case the caller keeps the plain
 *          0 → totalMi arc axis.
 */
export function resolveMilepostAxis({ speedSegments, totalMi, lrs, direction } = {}) {
  const total = Number(totalMi);
  if (!Number.isFinite(total) || total <= 0) return null;

  let anchors = segmentAnchors(speedSegments, total);

  // No segment carries a milepost → fall back to the road/milepost range the
  // user typed on the parent route, spread linearly over the lane.
  if (anchors.length < 2) {
    const range = pathMilepostRange(lrs);
    if (!range) return null;
    anchors = [
      [0, range[0]],
      [total, range[1]],
    ];
  }

  // Bad LRS data (a milepost that jumps backwards mid-lane) would make the
  // axis non-invertible and place zones wrongly. Arc miles are safer.
  if (!strictlyMonotone(anchors)) return null;

  const toAxis = piecewise(anchors);
  const beginMile = toAxis(0);
  const endMile = toAxis(total);
  if (!Number.isFinite(beginMile) || !Number.isFinite(endMile)) return null;
  if (Math.abs(endMile - beginMile) < 1e-9) return null;

  // Billing meters `end - begin` for P and `begin - end` for N. An axis whose
  // order contradicts the declared direction would bill NEGATIVE miles, so
  // fall back to arc rather than emit it.
  const dir = String(direction || '').trim().toUpperCase();
  if (dir === 'P' && endMile <= beginMile) return null;
  if (dir === 'N' && endMile >= beginMile) return null;

  return { beginMile, endMile, toAxis };
}

export default resolveMilepostAxis;
